import { Viewer } from "@photo-sphere-viewer/core";
import { AutorotatePlugin } from "@photo-sphere-viewer/autorotate-plugin";
import { GalleryPlugin } from "@photo-sphere-viewer/gallery-plugin";
import { CompassPlugin } from "@photo-sphere-viewer/compass-plugin";
import { GyroscopePlugin } from "@photo-sphere-viewer/gyroscope-plugin";
import { MarkersPlugin } from "@photo-sphere-viewer/markers-plugin";
import { VirtualTourPlugin } from "@photo-sphere-viewer/virtual-tour-plugin";
import { VideoPlugin } from "@photo-sphere-viewer/video-plugin";
import { VisibleRangePlugin } from "@photo-sphere-viewer/visible-range-plugin";
import { SettingsPlugin } from "@photo-sphere-viewer/settings-plugin";
import { EquirectangularVideoAdapter } from "@photo-sphere-viewer/equirectangular-video-adapter";

import "@photo-sphere-viewer/core/index.css";
import "@photo-sphere-viewer/gallery-plugin/index.css";
import "@photo-sphere-viewer/compass-plugin/index.css";
import "@photo-sphere-viewer/markers-plugin/index.css";
import "@photo-sphere-viewer/virtual-tour-plugin/index.css";
import "@photo-sphere-viewer/video-plugin/index.css";
import "@photo-sphere-viewer/settings-plugin/index.css";
import "./style.css";

/**
 * Start age-gate script
 */

// Shared helper: initialise a single viewer container from its data-settings
function initViewerElement(el) {
  // Destroy any previous PSV instance (handles Elementor editor re-renders)
  if (el.dataset.psvInitialized && el.__psvViewer) {
    try {
      el.__psvViewer.destroy();
    } catch (e) {
      /* noop */
    }
  }
  el.dataset.psvInitialized = "1";

  var settings =
    typeof jQuery !== "undefined"
      ? jQuery(el).data("settings")
      : JSON.parse(el.getAttribute("data-settings") || "{}");

  if (!settings) return;

  var navbar = settings.navbar || false;
  var navbarStr = Array.isArray(navbar)
    ? navbar.join(",")
    : String(navbar || "");

  // Build plugins array
  var plugins = buildPlugins(settings, navbarStr);

  // Build viewer config – always pass the actual DOM element as container
  var config = {
    ...settings,
    container: el,
    navbar: navbar,
    plugins: plugins,
  };

  // If this is a video viewer, swap in the video adapter
  if (settings.isVideo && settings.videoSource) {
    config.adapter = EquirectangularVideoAdapter;
    config.panorama = { source: settings.videoSource };
  }

  // Remove our custom keys so PSV doesn't choke on them
  delete config.galleryPlugin;
  delete config.compassPlugin;
  delete config.gyroscopePlugin;
  delete config.visibleRangePlugin;
  delete config.virtualTourPlugin;
  delete config.videoPlugin;
  delete config.videoSource;
  delete config.isVideo;

  // Map legacy v4 keys to PSV v5 keys
  if (config.defaultLong !== undefined) {
    config.defaultYaw = config.defaultLong + "deg";
    delete config.defaultLong;
  }
  if (config.defaultLat !== undefined) {
    config.defaultPitch = config.defaultLat + "deg";
    delete config.defaultLat;
  }

  // Defer creation to next frame so the browser has finished laying out the
  // container (fixes 0-dimension containers in Elementor columns).
  requestAnimationFrame(function () {
    if (el.__psvViewer) return; // another init beat us here
    el.__psvViewer = new Viewer(config);
  });
}

/**
 * Build the PSV plugins array from data-settings.
 */
function buildPlugins(settings, navbarStr) {
  var plugins = [];

  // Autorotate
  if (navbarStr.includes("autorotate")) {
    plugins.push(
      AutorotatePlugin.withConfig({
        autostartDelay: 1000,
        autorotatePitch: "5deg",
      }),
    );
  }

  // Gallery
  var gc = settings.galleryPlugin;
  if (gc && gc.items && gc.items.length) {
    plugins.push(
      GalleryPlugin.withConfig({
        items: gc.items,
        visibleOnLoad: !!gc.visibleOnLoad,
        hideOnClick: !!gc.hideOnClick,
        navigationArrows: !!gc.navigationArrows,
        thumbnailSize: gc.thumbnailSize || { width: 200, height: 100 },
      }),
    );
  }

  // Compass (premium)
  var cc = settings.compassPlugin;
  if (cc) {
    plugins.push(
      CompassPlugin.withConfig({
        size: cc.size || "120px",
        navigation: true,
      }),
    );
  }

  // Gyroscope (premium)
  if (settings.gyroscopePlugin) {
    plugins.push(GyroscopePlugin);
  }

  // Visible Range (premium)
  var vr = settings.visibleRangePlugin;
  if (vr) {
    plugins.push(
      VisibleRangePlugin.withConfig({
        horizontalRange: vr.horizontalRange || null,
        verticalRange: vr.verticalRange || null,
      }),
    );
  }

  // Virtual Tour (premium)
  var vt = settings.virtualTourPlugin;
  if (vt && vt.nodes && vt.nodes.length) {
    // Markers plugin is required when renderMode is 'markers'
    if (vt.renderMode === "markers") {
      plugins.push(MarkersPlugin);
    }
    plugins.push(
      VirtualTourPlugin.withConfig({
        nodes: vt.nodes,
        startNodeId: vt.startNodeId || vt.nodes[0].id,
        renderMode: vt.renderMode || "3d",
      }),
    );
  }

  // Video (premium)
  var vp = settings.videoPlugin;
  if (vp) {
    plugins.push(SettingsPlugin);
    plugins.push(
      VideoPlugin.withConfig({
        muted: !!vp.muted,
      }),
    );
  }

  return plugins;
}

(function ($) {
  "use strict";

  var PhotoSphereViewer = function ($scope, $) {
    var $container = $scope.find(".photo-sphere-viewer");

    if (!$container.length) {
      return;
    }

    $.each($container, function () {
      initViewerElement(this);
    });
  };

  jQuery(window).on("elementor/frontend/init", function () {
    var elementorFrontend = window.elementorFrontend;
    if (elementorFrontend && elementorFrontend.hooks) {
      elementorFrontend.hooks.addAction(
        "frontend/element_ready/photo-sphere-viewer.default",
        PhotoSphereViewer,
      );
      elementorFrontend.hooks.addAction(
        "frontend/element_ready/psv-virtual-tour.default",
        PhotoSphereViewer,
      );
      elementorFrontend.hooks.addAction(
        "frontend/element_ready/psv-video-360.default",
        PhotoSphereViewer,
      );
    }
  });

  // Fallback: initialise any Elementor widget viewers that were NOT handled
  // by Elementor's frontend handler (only runs on non-Elementor pages).
  jQuery(function () {
    if (window.elementorFrontend && window.elementorFrontend.hooks) return;
    document
      .querySelectorAll(".photo-sphere-viewer[data-settings]")
      .forEach(function (el) {
        if (!el.dataset.psvInitialized) {
          initViewerElement(el);
        }
      });
  });
})(jQuery);

/**
 * End age-gate script
 */

// init shortcodes scripts
document.querySelectorAll(".xero-psv-container").forEach(function (wrapper) {
  var el = wrapper.firstElementChild;
  if (!el || el.dataset.psvInitialized) return;
  el.dataset.psvInitialized = "1";

  var settings = el.getAttribute("data-settings");
  var parsedSettings = JSON.parse(settings || "{}");

  // Ensure navbar is ordered consistently for shortcode viewers
  var desiredOrder = [
    "zoom",
    "move",
    "autorotate",
    "caption",
    "download",
    "fullscreen",
    "description",
  ];

  var navbar = parsedSettings.navbar;

  if (navbar == "") {
    navbar = false;
  } else if (Array.isArray(navbar)) {
    navbar = navbar.slice().sort(function (a, b) {
      return desiredOrder.indexOf(a) - desiredOrder.indexOf(b);
    });
  } else if (typeof navbar === "string" && navbar.length > 0) {
    var parts = navbar.split(",");
    navbar = parts.sort(function (a, b) {
      return desiredOrder.indexOf(a) - desiredOrder.indexOf(b);
    });
  }

  // Map legacy v4 keys to PSV v5 keys
  if (parsedSettings.defaultLong !== undefined) {
    parsedSettings.defaultYaw = parsedSettings.defaultLong + "deg";
    delete parsedSettings.defaultLong;
  }
  if (parsedSettings.defaultLat !== undefined) {
    parsedSettings.defaultPitch = parsedSettings.defaultLat + "deg";
    delete parsedSettings.defaultLat;
  }

  if (el) {
    var scNavbarStr = Array.isArray(navbar) ? navbar.join(",") : "";
    var scPlugins = buildPlugins(parsedSettings, scNavbarStr);

    delete parsedSettings.galleryPlugin;
    delete parsedSettings.compassPlugin;
    delete parsedSettings.gyroscopePlugin;
    delete parsedSettings.visibleRangePlugin;
    delete parsedSettings.virtualTourPlugin;
    delete parsedSettings.videoPlugin;
    delete parsedSettings.videoSource;
    delete parsedSettings.isVideo;

    new Viewer({
      ...parsedSettings,
      container: el,
      navbar: Array.isArray(navbar) ? navbar : false,
      plugins: scPlugins,
    });
  }
});
